import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Response, Pagination, ChangeLog } from '@ui/src';
import { PspIntegration, ExternalPspPartner } from '../../types';
import {
  ExternalAccount,
  ExternalAccountTopUpOption,
} from '../../external-accounts/types';
import { LinkedClient } from '../../linked-clients/types';
import { customersComponentApi } from '../../../customers/store/services/customers';

export interface CustomersExternalAccounstListState {
  pagination?: Pagination;
  externalAccounts?: ExternalAccount[];
  externalAccountsErrorsList?: string;
  externalAccount?: ExternalAccount;
  pspPartners?: ExternalPspPartner[];
  takeCount?: number;
  pspIntegrations?: PspIntegration[];
  openPayNotificationVisible?: boolean;
  externalAccountTopUpOptions?: ExternalAccountTopUpOption[];
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  linkedClients?: LinkedClient[];
  linkedClientsPagination?: Pagination;
  linkedClientsErrorsList?: string;
}

const initialState: CustomersExternalAccounstListState = {};

export const customersExternalAccountsListSlice = createSlice({
  name: 'customersExternalAccountsList',
  initialState,
  reducers: {
    clearExternalAccounts: (state) => {
      state.externalAccounts = undefined;
      state.externalAccountsErrorsList = undefined;
      state.pagination = undefined;
      state.takeCount = undefined;
      state.pspPartners = undefined;
      state.linkedClients = undefined;
      state.linkedClientsPagination = undefined;
      state.linkedClientsErrorsList = undefined;
    },
    clearAccountsState: (state) => {
      state.externalAccounts = undefined;
      state.pagination = undefined;
      state.takeCount = undefined;
      state.externalAccountsErrorsList = undefined;
    },
    clearLinkedClientsState: (state) => {
      state.linkedClients = undefined;
      state.linkedClientsPagination = undefined;
      state.linkedClientsErrorsList = undefined;
    },
    clearExternalAccountPanel: (state) => {
      state.externalAccount = undefined;
      state.externalAccountTopUpOptions = undefined;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    setOpenPayNotificationVisible: (state, action: PayloadAction<boolean>) => {
      state.openPayNotificationVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalAccountsList
        .matchPending,
      (state) => {
        state.externalAccountsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalAccountsList
        .matchRejected,
      (state, action: any) => {
        state.externalAccounts = undefined;
        state.pagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.externalAccountsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.externalAccountsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalAccountsList
        .matchFulfilled,
      (state, action: PayloadAction<Response<any>>) => {
        state.externalAccounts = action?.payload?.data?.edges?.map((item) => ({
          pspPartnerId: '-',
          currencyId: '-',
          paymentType: '-',
          accountId: '-',
          accountName: '-',
          accountType: '-',
          active: false,

          ...item.node,
        }));
        state.pagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalAccountChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalAccountTopUpOptions
        .matchFulfilled,
      (state, action: PayloadAction<Response<ExternalAccountTopUpOption>>) => {
        state.externalAccountTopUpOptions = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalOrganizations
        .matchPending,
      (state) => {
        state.linkedClientsErrorsList = undefined;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalOrganizations
        .matchRejected,
      (state, action: any) => {
        state.linkedClients = undefined;
        state.linkedClientsPagination = undefined;

        if (action?.payload.data) {
          if (Array.isArray(action?.payload.data?.message)) {
            state.linkedClientsErrorsList = action?.payload.data?.message
              ?.map((item: { value: string }) => item.value)
              .join('; ');
          } else {
            state.linkedClientsErrorsList = action?.payload.data?.error;
          }
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalOrganizations
        .matchFulfilled,
      (state, action: PayloadAction<Response<LinkedClient>>) => {
        state.linkedClients = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );

        state.linkedClientsPagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomerExternalAccountCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (item.id === action.payload.data.id) {
            result.currenciesIds = action.payload.data.currenciesIds;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersPspPartners.matchFulfilled,
      (state, action: PayloadAction<Response<ExternalPspPartner>>) => {
        state.pspPartners = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExternalAccount
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccount }>) => {
        state.externalAccount = action.payload.data;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.bindExternalOrganization.matchFulfilled,
      (state, action: PayloadAction<{ data: LinkedClient }>) => {
        let nextLinkedClients: LinkedClient[] = [];

        if (state.linkedClients) {
          nextLinkedClients = ([] as LinkedClient[]).concat(
            state.linkedClients,
          );
        }

        nextLinkedClients.push(action.payload.data);

        state.linkedClients = nextLinkedClients;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersPspIntegrations
        .matchFulfilled,
      (state, action: PayloadAction<Response<PspIntegration>>) => {
        state.pspIntegrations = action?.payload?.data?.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomerPaymentAccounts
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExternalAccountTopUpOption }>) => {
        state.externalAccountTopUpOptions =
          state?.externalAccountTopUpOptions?.filter(
            (item) => item.id !== action.payload?.data?.id,
          );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersExternalAccountStatus
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                active: boolean;
                ids: string[];
              };
            };
          };
        },
      ) => {
        const nextExternalAccounts = state.externalAccounts?.map((item) => {
          const result = { ...item };

          if (result.id === action?.meta.arg?.originalArgs?.ids[0]) {
            result.active = action?.meta.arg?.originalArgs.active;
          }

          return result;
        });

        state.externalAccounts = nextExternalAccounts;
        state.externalAccount = {
          ...state.externalAccount,
          active: action?.meta.arg?.originalArgs.active,
        } as ExternalAccount;
      },
    );
  },
});

export const {
  setTakeCount,
  clearExternalAccounts,
  clearExternalAccountPanel,
  setOpenPayNotificationVisible,
  clearAccountsState,
  clearLinkedClientsState,
} = customersExternalAccountsListSlice.actions;

export default customersExternalAccountsListSlice.reducer;
